import { IExportedFile, IPaginated, IPaginationSearch, TableExportFormat } from '@shared/common';

import { CompanyType } from '../../../companyService/company/types/enums';
import { StatusContractInspection } from '../../contracts/types/enums';
import { InspectionFlowService } from '../../inspectionFlows/types/enums';
import { StatusInspection } from '../../inspections/types/enums';

import {
  UsageByLocationSortField,
  UsageDateKind,
  UsageLocationGroupBy,
  UsageRecordsSortField,
  UsageRowSource,
  UsageServiceBucket,
  UsageStage,
  UsageTrendBucket,
  UsageUnit,
  UsageVerticalKey,
} from './enums';

/**
 * One row of the vertical registry. Adding a vertical = adding a row.
 * @interface
 * @property {UsageVerticalKey} key - Short filter key.
 * @property {CompanyType} companyType - Company type stored on `inspections.company_type` / `rental_contract`.
 * @property {UsageUnit} unit - What a "record" is for record-level tiles and export row identity.
 * @property {string} label - Human label.
 */
export interface IUsageVertical {
  key: UsageVerticalKey;
  companyType: CompanyType;
  unit: UsageUnit;
  label: string;
}

/** Vertical registry (config-driven). Service-level metrics always derive from inspections + service rounds. */
export const USAGE_VERTICALS: readonly IUsageVertical[] = [
  { key: UsageVerticalKey.DEALER, companyType: CompanyType.DEALER, unit: UsageUnit.INSPECTION, label: 'Dealer' },
  { key: UsageVerticalKey.LEASING, companyType: CompanyType.LEASING, unit: UsageUnit.INSPECTION, label: 'Leasing' },
  {
    key: UsageVerticalKey.SELF_INSPECTION,
    companyType: CompanyType.SELF_INSPECTION,
    unit: UsageUnit.INSPECTION,
    label: 'Self Inspection',
  },
  { key: UsageVerticalKey.RENTAL, companyType: CompanyType.RENTAL, unit: UsageUnit.CONTRACT, label: 'Rental' },
  { key: UsageVerticalKey.FLOORING, companyType: CompanyType.FLOORING, unit: UsageUnit.INSPECTION, label: 'Flooring' },
];

/**
 * Verticals served by the core (inspection-based) usage endpoints. Rental is a contract-shaped
 * companion track and is NOT part of the core service rows.
 */
export const USAGE_INSPECTION_VERTICALS: readonly IUsageVertical[] = USAGE_VERTICALS.filter(
  v => v.unit === UsageUnit.INSPECTION,
);

/** Resolve a vertical by its short key. */
export const findUsageVertical = (key: string): IUsageVertical | undefined => USAGE_VERTICALS.find(v => v.key === key);

/**
 * Stage → inspection statuses. Verbatim copy of the hub's `inspectionLifecycleStages`.
 * `EXPIRED` is never persisted (computed from `expiry`), so expired rows fall under `awaiting`.
 */
export const USAGE_STAGE_STATUSES: Readonly<Record<UsageStage, readonly StatusInspection[]>> = {
  [UsageStage.AWAITING]: [
    StatusInspection.NOT_STARTED,
    StatusInspection.SENT,
    StatusInspection.ON_HOLD,
    StatusInspection.EXPIRED,
  ],
  [UsageStage.CAPTURE]: [StatusInspection.INITIATED, StatusInspection.IN_PROGRESS],
  [UsageStage.REVIEW]: [StatusInspection.IN_REVIEW, StatusInspection.RE_DO],
  [UsageStage.COMPLETED]: [StatusInspection.COMPLETED, StatusInspection.REQUIRES_ATTENTION],
  [UsageStage.CLOSED]: [StatusInspection.CANCELED, StatusInspection.DELETED],
};

/** Stage → rental contract statuses (companion track). */
export const USAGE_CONTRACT_STAGE_STATUSES: Readonly<Record<UsageStage, readonly StatusContractInspection[]>> = {
  [UsageStage.AWAITING]: [
    StatusContractInspection.SCHEDULED,
    StatusContractInspection.PENDING_PICK_UP,
    StatusContractInspection.MISSED_PICK_UP,
  ],
  [UsageStage.CAPTURE]: [StatusContractInspection.PENDING_DROP_OFF, StatusContractInspection.PENDING_EMPLOYEE],
  [UsageStage.REVIEW]: [StatusContractInspection.ESTIMATE_REQUESTED],
  [UsageStage.COMPLETED]: [StatusContractInspection.COMPLETED],
  [UsageStage.CLOSED]: [StatusContractInspection.CANCELED, StatusContractInspection.DELETED],
};

/** Stages that count as "in progress". */
export const USAGE_IN_PROGRESS_STAGES: readonly UsageStage[] = [
  UsageStage.AWAITING,
  UsageStage.CAPTURE,
  UsageStage.REVIEW,
];

/**
 * Legacy service tokens still present in `inspections.additional_data.services` snapshots
 * (pre-normalisation data), mapped the same way migration `1775553344567` normalised flows.
 * `null` = not a service (modifier / diagnostics), ignore.
 */
export const USAGE_LEGACY_SERVICE_ALIASES: Readonly<Record<string, InspectionFlowService | null>> = {
  CR: InspectionFlowService.AIwH,
  AI: InspectionFlowService.AIwH,
  AIW: InspectionFlowService.TIRE,
  OBD: null,
};

/** Normalise a raw services snapshot (legacy tokens included) to the current enum, deduplicated. */
export const normalizeUsageServices = (raw: readonly string[] | null | undefined): InspectionFlowService[] => {
  const out = new Set<InspectionFlowService>();
  for (const token of raw ?? []) {
    if ((Object.values(InspectionFlowService) as string[]).includes(token)) {
      out.add(token as InspectionFlowService);
      continue;
    }
    const alias = USAGE_LEGACY_SERVICE_ALIASES[token];
    if (alias) out.add(alias);
  }
  return Array.from(out);
};

export const USAGE_SERVICE_BUCKET_LABELS: Readonly<Record<UsageServiceBucket, string>> = {
  [UsageServiceBucket.VISUAL]: 'Visual-only',
  [UsageServiceBucket.AI_ONLY]: 'AI-only',
  [UsageServiceBucket.AI_HUMAN]: 'AI + Human Review',
  [UsageServiceBucket.ESTIMATE]: 'Estimate + CR',
};

export const USAGE_STAGE_LABELS: Readonly<Record<UsageStage, string>> = {
  [UsageStage.AWAITING]: 'Awaiting client',
  [UsageStage.CAPTURE]: 'Capture',
  [UsageStage.REVIEW]: 'Review',
  [UsageStage.COMPLETED]: 'Completed',
  [UsageStage.CLOSED]: 'Cancelled',
};

/**
 * Shared filter honoured by every usage endpoint. Dates are inclusive calendar days
 * interpreted in `timezone` (default `UTC`); the period is `[dateFrom 00:00, dateTo 24:00)`.
 * Company is honoured for `ALL_TENANTS` principals only; everybody else is pinned by
 * `resolveLocationScope`. Location ids are further narrowed by the caller's scope.
 * @interface
 */
export interface IUsageAnalyticsFilter {
  dateFrom: string;
  dateTo: string;
  timezone?: string;
  verticals?: UsageVerticalKey[];
  companyId?: number;
  locationIds?: number[];
  services?: UsageServiceBucket[];
  stages?: UsageStage[];
}

/**
 * Trend params: shared filter + bucket granularity.
 * @interface
 */
export interface IUsageTrendParams extends IUsageAnalyticsFilter {
  bucket?: UsageTrendBucket;
}

/** Echo of the resolved period. */
export interface IUsagePeriod {
  dateFrom: string;
  dateTo: string;
  timezone: string;
}

/** Requested / completed pair. */
export interface IUsageCounts {
  requested: number;
  completed: number;
}

/**
 * Current stage of the rows REQUESTED in the period (a cohort snapshot). Always reconciles:
 * `requested = completed + inProgress + cancelled` and `inProgress = awaiting + capture + review`.
 * @interface
 */
export interface IUsageCohortBreakdown {
  completed: number;
  inProgress: number;
  awaiting: number;
  capture: number;
  review: number;
  cancelled: number;
}

/**
 * Average and median of a duration in minutes over `count` rows; `null` when nothing qualifies.
 * @interface
 */
export interface IUsageDurationStats {
  avgMinutes: number | null;
  medianMinutes: number | null;
  count: number;
}

/**
 * Inspection-level tiles, counted on the inspection's BASE row only (an estimate / upgrade round
 * completing later does not re-complete the inspection).
 * @interface
 * @property {number} requested - Distinct inspections whose base row was requested (`created_at`) in the period.
 * @property {number} completed - Distinct inspections whose base row completed (`service_completed_at`) in the period.
 * @property {number} inProgress - Requested cohort currently in awaiting / capture / review (sum of the three).
 * @property {number} cancelled - Requested cohort currently closed.
 * @property {number} attention - Requested cohort currently in `REQUIRES_ATTENTION`.
 * @property {IUsageDurationStats} captureDuration - Client capture time `completed_at − COALESCE(started_at, created_at)` over inspections completed in the period.
 */
export interface IUsageInspectionsBreakdown {
  requested: number;
  completed: number;
  inProgress: number;
  awaiting: number;
  capture: number;
  review: number;
  cancelled: number;
  attention: number;
  captureDuration: IUsageDurationStats;
}

/**
 * Current inspection status of the requested cohort (distinct inspections), with its stage.
 * @interface
 */
export interface IUsageStatusBreakdownItem {
  status: StatusInspection;
  stage: UsageStage;
  inspections: number;
}

/**
 * Dashboard summary tiles.
 * `requested` and `completed` are two independent, date-bucketed series over the period
 * (a June request completed in July is June-requested and July-completed).
 * @interface
 * @property {number} requested - Service rows whose requested date ∈ period.
 * @property {number} completed - Service rows whose completed date ∈ period (stage = completed).
 * @property {number | null} completionRate - `completed / requested`, `null` when nothing requested.
 * @property {IUsageCohortBreakdown} cohort - Current stage of the requested cohort.
 * @property {number} attention - Distinct inspections in `REQUIRES_ATTENTION` among the requested cohort.
 * @property {object} records - Distinct inspections touched by ANY usage row requested / completed in the period.
 * @property {IUsageInspectionsBreakdown} inspections - Inspection-level tiles on the BASE row (see the type).
 * @property {IUsageStatusBreakdownItem[]} statuses - Requested cohort by current inspection status.
 * @property {IUsageDurationStats} turnaround - Service turnaround `completed_at − requested_at` over service rows completed in the period.
 */
export interface IUsageSummaryResult {
  period: IUsagePeriod;
  requested: number;
  completed: number;
  completionRate: number | null;
  cohort: IUsageCohortBreakdown;
  attention: number;
  records: { inspections: IUsageCounts };
  inspections: IUsageInspectionsBreakdown;
  statuses: IUsageStatusBreakdownItem[];
  turnaround: IUsageDurationStats;
}

/** Per-service breakdown row. */
export interface IUsageByServiceItem {
  service: UsageServiceBucket;
  requested: number;
  completed: number;
  completionRate: number | null;
  inProgress: number;
  cancelled: number;
  turnaround: IUsageDurationStats;
}

export interface IUsageByServiceResult {
  period: IUsagePeriod;
  items: IUsageByServiceItem[];
}

/**
 * Per-location breakdown row (or per-company row when `groupBy = company`, then `locationId` is null).
 * Names are enriched by the gateway when available.
 */
export interface IUsageByLocationItem {
  companyId: number;
  locationId: number | null;
  requested: number;
  completed: number;
  completionRate: number | null;
  inProgress: number;
  cancelled: number;
  companyName?: string;
  locationName?: string;
}

/**
 * Paginated by-location breakdown. Σ over ALL pages reconciles to the summary for the same filter.
 * @interface
 */
export interface IUsageByLocationResult extends IPaginated<IUsageByLocationItem[]> {
  period: IUsagePeriod;
  groupBy: UsageLocationGroupBy;
}

/**
 * By-location list request: shared filter + pagination + server-side sort (default `completed DESC`)
 * + `groupBy` (default `location`; `company` rolls the groups up per company, `locationId` sort then orders by company).
 * Location-name search is a gateway concern (names live in company-service) and is not part of this contract.
 * @interface
 */
export interface IPaginationSearchUsageByLocation extends IPaginationSearch, IUsageAnalyticsFilter {
  sortBy?: UsageByLocationSortField;
  groupBy?: UsageLocationGroupBy;
}

/**
 * One trend point; `bucket` is the local (timezone) bucket start as `YYYY-MM-DD`.
 * `requested` / `completed` count service rows; `inspectionsRequested` / `inspectionsCompleted` count
 * distinct inspections on their BASE row.
 */
export interface IUsageTrendPoint {
  bucket: string;
  requested: number;
  completed: number;
  inspectionsRequested: number;
  inspectionsCompleted: number;
}

export interface IUsageTrendResult {
  period: IUsagePeriod;
  bucket: UsageTrendBucket;
  items: IUsageTrendPoint[];
}

/**
 * One usage row (inspection × service) — the drill-down record and the export line.
 * An upgraded / re-estimated inspection yields several rows (one per source).
 * @interface
 * @property {boolean} requestedInPeriod - Requested date ∈ period.
 * @property {boolean} completedInPeriod - Completed date ∈ period.
 * @property {boolean} hasRowsOutsidePeriod - The same inspection has other usage rows dated outside the period ("scans in other periods").
 */
export interface IUsageRecordView {
  inspectionId: string;
  vin: string | null;
  companyId: number;
  locationId: number;
  companyType: CompanyType;
  vertical: UsageVerticalKey;
  source: UsageRowSource;
  roundNumber: number | null;
  service: UsageServiceBucket;
  stage: UsageStage;
  status: StatusInspection;
  requestedAt: string;
  completedAt: string | null;
  requestedInPeriod: boolean;
  completedInPeriod: boolean;
  hasRowsOutsidePeriod: boolean;
  companyName?: string;
  locationName?: string;
}

/**
 * Record-level search on top of the shared filter (records and export only — the tiles do not
 * accept these, so an export narrowed by them does not reconcile to the tiles).
 * @interface
 * @property {string} [vin] - Case-insensitive contains-match on `vin_number`.
 * @property {string} [inspectionId] - Case-insensitive contains-match on `inspection_id`.
 */
export interface IUsageRecordSearch {
  vin?: string;
  inspectionId?: string;
}

/**
 * Drill-down record search. `dateKind` picks the series; omitted = rows requested OR completed in the period.
 * @interface
 */
export interface IPaginationSearchUsageRecords extends IPaginationSearch, IUsageAnalyticsFilter, IUsageRecordSearch {
  dateKind?: UsageDateKind;
  sortBy?: UsageRecordsSortField;
}

/**
 * Export request: same filter as the dashboard so the file reconciles to the tiles.
 * @interface
 */
export interface IExportUsageRecords extends IUsageAnalyticsFilter, IUsageRecordSearch {
  format: TableExportFormat;
  dateKind?: UsageDateKind;
}

export interface IExportedUsageRecordsFile extends IExportedFile {}

/** Export column keys (stable header order). */
export enum UsageExportColumn {
  VIN = 'vin',
  INSPECTION_ID = 'inspectionId',
  DATE_REQUESTED = 'dateRequested',
  DATE_COMPLETED = 'dateCompleted',
  SERVICE = 'service',
  STATUS = 'status',
  SOURCE = 'source',
  ROUND = 'round',
  VERTICAL = 'vertical',
  COMPANY_ID = 'companyId',
  COMPANY_NAME = 'companyName',
  LOCATION_ID = 'locationId',
  LOCATION_NAME = 'locationName',
  REQUESTED_IN_PERIOD = 'requestedInPeriod',
  COMPLETED_IN_PERIOD = 'completedInPeriod',
  OTHER_PERIODS = 'otherPeriods',
}

/**
 * A named thing on an analytics surface and what it means: one annotation, not a label map beside
 * a description map — parallel records drift, and the half that drifts is the one nobody reads.
 *
 * This is contract knowledge rather than UI copy. Whoever renders the thing — the hub today, a
 * client portal tomorrow — explains it from `description` instead of inventing its own words, so
 * two surfaces cannot end up describing the same number differently.
 */
export interface IAnalyticsAnnotation {
  label: string;
  description: string;
}

/** One annotated definition per export column: what it is called in the file, and what it holds. */
export type IUsageExportColumnAnnotation = IAnalyticsAnnotation;

/**
 * An annotation for a figure that is DERIVED rather than counted — a share, a rate, a duration.
 *
 * `formula` is the arithmetic in one line, and it earns its place only where prose cannot be exact
 * about it: "Completed ÷ Requested" settles which way round the division goes, where a sentence
 * can always be read backwards. A plain count does not get one — "Requested = requested" is noise.
 *
 * It is written in the words the surfaces already use for those values (the Records table's own
 * Requested / Completed columns), not in column names: the reader who needs it is the one deciding
 * whether to trust the number, not the one maintaining the query.
 */
export interface IAnalyticsMetricAnnotation extends IAnalyticsAnnotation {
  formula?: string;
  /**
   * A worked illustration, for figures whose formula is still abstract when read — a duration
   * between two events reads as arithmetic only once someone sees two clock times and the gap.
   *
   * Illustrative and fixed. Where a client can put the reader's OWN numbers through the formula it
   * should do that instead: the live sum is what settles a doubt about the figure on screen, and
   * the shared package cannot know it.
   */
  example?: string;
}

export const USAGE_EXPORT_COLUMNS: Readonly<Record<UsageExportColumn, IUsageExportColumnAnnotation>> = {
  [UsageExportColumn.VIN]: { label: 'VIN', description: 'The vehicle, when the inspection carries one.' },
  [UsageExportColumn.INSPECTION_ID]: {
    label: 'Inspection ID',
    description: 'The inspection this row belongs to; repeats across its services.',
  },
  [UsageExportColumn.DATE_REQUESTED]: { label: 'Date requested', description: 'When the row was asked for.' },
  [UsageExportColumn.DATE_COMPLETED]: {
    label: 'Date completed',
    description: 'When it finished; empty until it does.',
  },
  [UsageExportColumn.SERVICE]: {
    label: 'Service',
    description: 'Which of the four service buckets the row counts as.',
  },
  [UsageExportColumn.STATUS]: { label: 'Status', description: 'The lifecycle phase the row sits in now.' },
  [UsageExportColumn.SOURCE]: {
    label: 'Source',
    description: 'Where the row came from — the inspection itself, or a later round.',
  },
  [UsageExportColumn.ROUND]: {
    label: 'Round',
    description: 'Which round, for rows that come from one. Empty on the base row.',
  },
  [UsageExportColumn.VERTICAL]: {
    label: 'Vertical',
    description: 'The tenant vertical: dealer, leasing or self inspection.',
  },
  [UsageExportColumn.COMPANY_ID]: { label: 'Company ID', description: 'The owning company.' },
  [UsageExportColumn.COMPANY_NAME]: {
    label: 'Company',
    description: "The company's name, as company-service holds it.",
  },
  [UsageExportColumn.LOCATION_ID]: { label: 'Location ID', description: 'The owning location.' },
  [UsageExportColumn.LOCATION_NAME]: { label: 'Location', description: "The location's name." },
  [UsageExportColumn.REQUESTED_IN_PERIOD]: {
    label: 'Requested in period',
    description: 'Y when the row was asked for inside the selected dates.',
  },
  [UsageExportColumn.COMPLETED_IN_PERIOD]: {
    label: 'Completed in period',
    description: 'Y when it finished inside them. Both can be Y, or neither.',
  },
  [UsageExportColumn.OTHER_PERIODS]: {
    label: 'Other periods',
    description: 'Y when the same inspection also has rows dated outside the window.',
  },
};

/** What each `Source` value means — the column that decides why one inspection has several rows. */
export const USAGE_ROW_SOURCE_DESCRIPTIONS: Readonly<Record<UsageRowSource, string>> = {
  [UsageRowSource.BASE]: "The inspection's own capture — one per inspection.",
  [UsageRowSource.ESTIMATE_ROUND]: 'An estimate round. A re-estimate adds another row.',
  [UsageRowSource.UPGRADE_ROUND]: 'A service added after the fact, on its own row.',
};

/** What each service bucket covers. Pairs with `USAGE_SERVICE_BUCKET_LABELS`. */
export const USAGE_SERVICE_BUCKET_DESCRIPTIONS: Readonly<Record<UsageServiceBucket, string>> = {
  [UsageServiceBucket.VISUAL]: 'Capture only.',
  [UsageServiceBucket.AI_ONLY]: 'Expedited AI — a quick condition report, no estimate.',
  [UsageServiceBucket.AI_HUMAN]: 'AI damage detection with a human review.',
  [UsageServiceBucket.ESTIMATE]: 'A priced estimate with its condition report.',
};

/** What each lifecycle phase means for an inspection. Pairs with `USAGE_STAGE_LABELS`. */
export const USAGE_STAGE_DESCRIPTIONS: Readonly<Record<UsageStage, string>> = {
  [UsageStage.AWAITING]: 'Sent, paused or timed out — the client has not opened it.',
  [UsageStage.CAPTURE]: 'The client is working through the steps.',
  [UsageStage.REVIEW]: 'Submitted, or sent back for rework.',
  [UsageStage.COMPLETED]: 'The report came back — cleared or flagged.',
  [UsageStage.CLOSED]: 'Cancelled or deleted before an outcome.',
};

/**
 * The summary tiles, keyed by the field on {@link IUsageSummaryResult} each one reads.
 *
 * Every figure here is one of two kinds, and the difference is what people get wrong: a PERIOD
 * number counts rows whose own date falls in the window, while a COHORT number takes the rows
 * requested in the window and reports where they stand NOW — so it keeps moving after the period
 * has closed. The descriptions say which, because no formatting of the number can.
 */
export type UsageSummaryMetric =
  | 'requested'
  | 'completed'
  | 'completionRate'
  | 'cohort.inProgress'
  | 'cohort.cancelled'
  | 'turnaround'
  | 'inspections.completed'
  | 'inspections.inProgress'
  | 'inspections.attention'
  | 'inspections.captureDuration';

export const USAGE_SUMMARY_METRICS: Readonly<Record<UsageSummaryMetric, IAnalyticsMetricAnnotation>> = {
  completed: {
    label: 'Completed services',
    description:
      'Rows whose completion date falls inside the selected period, whenever they were asked for — a service requested last month and finished this week counts here. A base row completes with its inspection; a round completes when that round does.',
  },
  requested: {
    label: 'Requested services',
    description:
      'Rows first asked for inside the period: a base row is dated by its inspection, a round by when the round was raised. The breakdown underneath follows that same cohort to wherever it stands now.',
  },
  completionRate: {
    label: 'Completion rate',
    description:
      'The two are not the same rows — a period can finish more than it received — so this reads as throughput rather than as a success rate.',
    formula: 'Services completed ÷ Services requested',
  },
  'cohort.inProgress': {
    label: 'Services in progress',
    description:
      'Of the services requested in this period, those sitting in an open phase right now: awaiting the client, in capture, or in review. A live number over a fixed cohort, so it falls as the work lands.',
  },
  'cohort.cancelled': {
    label: 'Services cancelled',
    description: 'Of the services requested in this period, those now cancelled or deleted.',
  },
  turnaround: {
    label: 'Turnaround',
    description:
      'Taken over the rows that completed inside the period. Rows still open are not in it, so a long-running job only reaches this figure once it lands. The median leads because a handful of rows left open for weeks pull an average far past anything anyone experienced; the average is reported beside it.',
    formula: 'Completed − Requested, median',
    example: 'Requested Sep 1, 09:00 → Completed Sep 3, 15:00 = 2d 6h',
  },
  'inspections.completed': {
    label: 'Inspections completed',
    description: 'Inspections with at least one service completing inside the period, counted once each.',
  },
  'inspections.inProgress': {
    label: 'Inspections in progress',
    description:
      'Of the inspections requested in this period, those still in an open phase — counted once each, so an inspection with three open services counts once.',
  },
  'inspections.attention': {
    label: 'Requiring attention',
    description:
      'Inspections requested in this period that now sit at Requires attention: the report came back, but something on it needs a person.',
  },
  'inspections.captureDuration': {
    label: 'Capture time',
    description:
      "From the client opening the inspection to submitting it, over the inspections that completed inside the period. Where no open timestamp was recorded, creation stands in for it, and an inspection that was never submitted is left out. Only the inspection's own capture counts — estimate and upgrade rounds have no capture of their own. The median leads: one session left open overnight moves an average by hours and the median not at all.",
    formula: 'Submitted − Opened, median',
    example: 'Opened 09:12 → Submitted 09:30 = 18m',
  },
};
